/**
 * Create Blog Feature
 * 100% Isolated: Create blog functionality that can be changed independently
 */
import { useState, useCallback } from 'react';
import { blogApiService, mapBackendBlogToPost } from '../api';
import { userService } from '../../user/service';
import type { CreateBlogRequest, BlogPost } from '../api';

export const useCreateBlog = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createBlog = useCallback(async (blogData: CreateBlogRequest): Promise<BlogPost> => {
    setLoading(true);
    setError(null);

    try {
      // Check authentication
      if (!userService.isAuthenticated()) {
        throw new Error('Authentication required to create a blog');
      }

      const response = await blogApiService.createBlog(blogData);
      const createdId = response.data?.blog_id;

      if (!response.success || !createdId) {
        throw new Error(response.error?.message || 'Failed to create blog post');
      }

      // Backend returns full blog on create — use it when available
      if (response.data?.title && response.data.content != null) {
        return mapBackendBlogToPost(response.data);
      }

      const blogResponse = await blogApiService.getBlogById(createdId, { preview: true });

      if (!blogResponse.success || !blogResponse.data) {
        throw new Error('Failed to fetch created blog post');
      }

      return blogResponse.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create blog';
      setError(errorMessage);
      throw err; // Re-throw the error instead of returning null
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createBlog,
    loading,
    error,
    clearError: () => setError(null)
  };
};
